import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatError } from "../../lib/api";
import { toast } from "sonner";
import { Bot, UserCheck, Check, Loader2, CheckCircle2, ArrowRight, Coins, Video, Phone, AlertTriangle } from "lucide-react";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`}>{children}</div>
);
const Field = ({ label, children }) => (
  <div><div className="text-xs font-bold uppercase tracking-wider text-ink-faint mb-2">{label}</div>{children}</div>
);

export default function Sponsor() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [panels, setPanels] = useState([]);
  const [slots, setSlots] = useState({ dates: [], slots: [], durations: [] });
  const [f, setF] = useState({ student_id: params.get("student") || "", role_id: "software-engineer", type: "ai", mode: "video", panel_id: "", date: "", time: "", duration: 45, phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const selStudent = students.find((x) => x.id === f.student_id);
  const isVoice = f.type === "ai" && f.mode === "voice";
  const studentPhone = selStudent?.phone || "";
  const phoneReady = isVoice ? (studentPhone.replace(/\D/g, "").length >= 8 || f.phone.replace(/\D/g, "").length >= 8) : true;

  useEffect(() => {
    api.get("/college/students").then((r) => setStudents(r.data));
    api.get("/catalog/job-roles").then((r) => setRoles(r.data));
    api.get("/catalog/slots").then((r) => setSlots(r.data));
  }, []);
  useEffect(() => {
    api.get(`/catalog/panels?type=${f.type}`).then((r) => {
      const list = f.type === "ai" ? r.data.panels : r.data.experts;
      setPanels(list); setF((s) => ({ ...s, panel_id: list[0]?.id || "" }));
    });
  }, [f.type]);

  const submit = async () => {
    if (!f.student_id || !f.panel_id || !f.date || !f.time) return toast.error("Please complete all fields");
    if (isVoice && !phoneReady) return toast.error("A verified phone number is required for a voice interview");
    setBusy(true);
    try {
      const payload = { ...f, mode: f.type === "ai" ? f.mode : undefined, phone: isVoice ? (studentPhone || f.phone) : undefined };
      const { data } = await api.post("/college/sponsor", payload);
      setDone(data);
      toast.success(`Interview sponsored · ${data.credits_remaining} credits left`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) {
    const s = students.find((x) => x.id === f.student_id);
    return (
      <div className="max-w-md mx-auto fade-up">
        <Card className="p-8 text-center">
          <div className="h-16 w-16 rounded-2xl bg-emerald-100 grid place-items-center mx-auto mb-4"><CheckCircle2 className="h-8 w-8 text-emerald-600" /></div>
          <h1 className="font-heading text-2xl font-bold text-ink">Interview Sponsored</h1>
          <p className="text-ink-soft text-sm mt-1">{s?.name} will see this interview after they log in.</p>
          <div className="mt-5 rounded-xl border border-slate-200 divide-y divide-slate-100 text-left">
            {[["Student", s?.name], ["Role", done.interview.role_name], ["Type", done.interview.type === "ai" ? "AI Interviews" : "Human Expert"], ["Mode", `${done.interview.mode_icon || "🎥"} ${done.interview.mode_label || ""}`], ["Date", done.interview.date], ["Time", done.interview.time], ["Credits used", `${done.interview.credits}`], ["Credits remaining", `${done.credits_remaining}`]].map(([k, v]) => (
              <div key={k} className="flex justify-between px-4 py-2.5 text-sm"><span className="text-ink-faint">{k}</span><span className="font-semibold text-ink">{v}</span></div>
            ))}
          </div>
          <div className="mt-6 flex gap-3">
            <button onClick={() => { setDone(null); }} className="flex-1 text-sm font-bold text-ink bg-slate-100 hover:bg-slate-200 px-5 py-3 rounded-full transition-colors">Sponsor Another</button>
            <button onClick={() => navigate("/college")} className="flex-1 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full transition-colors">Dashboard</button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Sponsor an Interview</h1>
        <p className="text-ink-soft text-sm mt-1">Schedule an interview for a student. Credits will be deducted from your college balance.</p>
      </div>
      <Card className="p-6 space-y-5">
        <Field label="Student">
          <select data-testid="sponsor-student" value={f.student_id} onChange={(e) => setF({ ...f, student_id: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand">
            <option value="">Select a student…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.student_code}</option>)}
          </select>
        </Field>
        <Field label="Target Job Role">
          <select data-testid="sponsor-role" value={f.role_id} onChange={(e) => setF({ ...f, role_id: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand">
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
        <Field label="Interview Type">
          <div className="grid grid-cols-2 gap-3">
            {[{ id: "ai", label: "AI Interviews", credits: 1, icon: Bot }, { id: "expert", label: "Human Expert Interview", credits: 2, icon: UserCheck }].map((t) => (
              <button key={t.id} data-testid={`sponsor-type-${t.id}`} onClick={() => setF({ ...f, type: t.id, mode: t.id === "ai" ? (f.mode || "video") : "human" })} className={`flex items-center gap-3 rounded-xl border p-3.5 transition-all ${f.type === t.id ? "border-brand ring-2 ring-brand/30" : "border-slate-200 hover:border-slate-300"}`}>
                <t.icon className="h-5 w-5 text-brand" />
                <div className="text-left"><div className="font-semibold text-ink text-sm">{t.label}</div><div className="text-xs text-ink-faint num flex items-center gap-1"><Coins className="h-3 w-3" /> {t.credits} credit{t.credits > 1 ? "s" : ""}</div></div>
              </button>
            ))}
          </div>
        </Field>
        {f.type === "ai" && (
          <Field label="AI Interview Mode">
            <div className="grid grid-cols-2 gap-3">
              {[{ id: "video", label: "AI Video Interview", icon: Video, emoji: "🎥" }, { id: "voice", label: "AI Voice Interview", icon: Phone, emoji: "📞" }].map((m) => (
                <button key={m.id} data-testid={`sponsor-mode-${m.id}`} onClick={() => setF({ ...f, mode: m.id })} className={`flex items-center gap-3 rounded-xl border p-3.5 transition-all ${f.mode === m.id ? "border-brand ring-2 ring-brand/30" : "border-slate-200 hover:border-slate-300"}`}>
                  <m.icon className="h-5 w-5 text-brand" />
                  <div className="text-left"><div className="font-semibold text-ink text-sm">{m.emoji} {m.label}</div></div>
                </button>
              ))}
            </div>
            {isVoice && (
              <div className="mt-3 rounded-xl border border-slate-200 p-3.5" data-testid="voice-phone-check">
                {studentPhone && studentPhone.replace(/\D/g, "").length >= 8 ? (
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Candidate phone number verified <span className="num text-ink-faint font-normal">({studentPhone})</span></div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 mb-2"><AlertTriangle className="h-4 w-4" /> No phone number on file — enter one to enable the call</div>
                    <input data-testid="sponsor-phone-input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+91 98765 43210"
                      className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm num focus:outline-none focus:ring-2 focus:ring-brand" />
                  </div>
                )}
              </div>
            )}
          </Field>
        )}
        <Field label="Panel / Interviewer">
          <select data-testid="sponsor-panel" value={f.panel_id} onChange={(e) => setF({ ...f, panel_id: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand">
            {panels.map((p) => <option key={p.id} value={p.id}>{p.name}{p.company ? ` · ${p.company}` : ""}</option>)}
          </select>
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Date">
            <select data-testid="sponsor-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm num focus:outline-none focus:ring-2 focus:ring-brand">
              <option value="">Select date…</option>
              {slots.dates.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Time">
            <select data-testid="sponsor-time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm num focus:outline-none focus:ring-2 focus:ring-brand">
              <option value="">Select time…</option>
              {slots.slots.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        <button data-testid="confirm-sponsor" onClick={submit} disabled={busy || (isVoice && !phoneReady)} className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3.5 rounded-full shadow-ambient disabled:opacity-60 transition-colors">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Confirm & Deduct Credit <ArrowRight className="h-4 w-4" /></>}
        </button>
      </Card>
    </div>
  );
}
